import 'p5/lib/addons/p5.sound';
import p5 from 'p5';
import sample from 'lodash/sample';

import colorAlpha from './color-alpha';
import frequencyToColor from './frequency-to-color';
import getPitch from './pitch-detection';
import mouseMovedInLast2Seconds, {
  updateMouseMoved,
} from './mouse-moved-in-last-2-seconds';
import {drawWaterColorBrush} from './watercolor';

export const DEFAULT_COLORS = [
  '#ffffff',
  'rgba(#ffffff, 0.69)',
  '#003e7e',
  '#b89d46',
  '#a5aa42',
  '#7ca34b',
  '#519d84',
  '#47aaa0',
  '#5493af',
  '#6351bb',
  '#7b3fa2',
  '#a33fa2',
];

export default function sketch(p) {
  let canvas;
  let mic;
  let fft;
  let bands = 64;
  let storedDataLen = 125;
  let spectrums;
  let currentSpectrum = 0;
  let amplitude;
  let space = 20;
  let trees = [];
  let treesByColor = {};
  let positions = [];

  function initializeStoredData() {
    return range(storedDataLen).map(x => ({
      spectrum: range(bands).map(x => 0),
    }));
  }

  function range(x) {
    let ret = [];
    for (let i = 0; i < x; i++) {
      ret.push(i);
    }
    return ret;
  }

  function rotate(arr, n) {
    return arr.slice(n, arr.length).concat(arr.slice(0, n));
  }

  p.setup = function setup() {
    canvas = p.createCanvas(window.innerWidth, window.innerHeight);
    mic = new p5.AudioIn();
    mic.start();
    amplitude = new p5.Amplitude();
    amplitude.setInput(mic);
    fft = new p5.FFT(0.8, bands);
    fft.setInput(mic);
    spectrums = initializeStoredData();
    p.orbitControl();
    p.background(0);

    const n_colors = DEFAULT_COLORS.length;
    DEFAULT_COLORS.forEach((color, index) => {
      treesByColor[color] = [];
    });

    let n = 0;

    for (
      let j = 2 * (p.height / 10), index = 0;
      j < p.height;
      j += p.height / 10, index++
    ) {
      positions[index] = DEFAULT_COLORS.map((color, index) => {
        const startOffset = p.width / n_colors;
        const totalWidth = p.width - p.width / n_colors;
        const position = startOffset + totalWidth / n_colors * index;
        const x = position;
        const y = j - p.height / 10;

        return {x, y};
      });
    }

    for (
      let j = 2 * (p.height / 10), jindex = 0;
      j < p.height;
      j += p.height / 10, jindex++
    ) {
      rotate(DEFAULT_COLORS, Math.floor(p.random(0, n_colors))).forEach(
        (color, index) => {
          for (let i = 0; i < 2; i++) {
            const {x, y} = positions[jindex][index];
            const tree = new TreeLifeCycle({
              color,
              x,
              y,
            });
            treesByColor[color].push(tree);
            trees.push(tree);
          }
        },
      );
    }
  };

  function drawSpectrum({spectrum, spectrumColor}) {
    p.noFill();
    p.stroke((spectrumColor === 'black' ? 'white' : spectrumColor) || 'white');
    p.beginShape();
    p.curveVertex(0, 0);
    for (let i = 0; i < bands; i++) {
      let endX = p.map(i, 0, bands, 0, p.width);
      let endY = p.height - p.map(spectrum[i], 0, 255, 0, p.height);
      p.curveVertex(endX, endY);
    }
    p.curveVertex(p.width, 0);
    p.endShape();
  }

  let level;
  let rotation = 0;
  let currentColor;
  p.draw = function draw() {
    p.background(colorAlpha(p, 0, 0.5));

    const cZ = 1000;
    const cX = p.mouseX - p.width / 2;
    const cY = p.mouseY - p.height / 2;

    level = amplitude.getLevel();
    const size = 80 + 200 * level;

    p.translate(p.width / 2, p.height / 2);
    // p.rotate(rotation);

    const spectrum = fft.analyze();
    const waveform = fft.waveform(1024, 'float32');
    const freq = getPitch(p, waveform);
    const fToColor = frequencyToColor(p, freq, level, {
      colors: DEFAULT_COLORS,
    });
    let {color, noteName, targetFrequency} = fToColor;

    if (color === 'black') {
      color = 'white';
    }

    currentColor = color;

    if (treesByColor[color]) {
      treesByColor[color].forEach(t => t.growTree());
    }

    trees.forEach(tree => {
      tree.update();
      tree.display();
    });

    rotation += 0.01;

    updateMouseMoved(p);
  };

  const NOISE_GATE = 0.01;

  class TreeLifeCycle {
    constructor({color, x, y}) {
      const scaling = level * 800;
      const basis = p.width / 2;
      this.x = x || p.width / 2;
      this.y = y || p.height / 2;
      this.size = 30;
      this.velocityX = 0;
      this.velocityY = 0;
      this.color = color;

      this.initialX = this.x;
      this.initialY = this.y;
    }

    update() {
      this.velocityX = p.lerp(this.velocityX, 0, 0.05);
      this.velocityY = p.lerp(this.velocityY, 0, 0.05);

      this.x = p.lerp(this.x, this.initialX, 0.05);
      this.y = p.lerp(this.y, this.initialY, 0.05);

      this.x += this.velocityX;
      this.y += this.velocityY;

      // if (this.x > this.initialX + 30) {
      //   this.velocityX = -this.velocityX;
      // }
      // if (this.y > this.initialY + 30) {
      //   this.velocityY = -this.velocityY;
      // }
      // if (this.x < this.initialX - 30) {
      //   this.velocityX = -this.velocityX;
      // }
      // if (this.y < this.initialY - 30) {
      //   this.velocityY = -this.velocityY;
      // }

      if (currentColor === this.color) {
        this.size = p.lerp(this.size, level * 200, 1);
      } else {
        this.size = p.lerp(this.size, 10, 0.1);
      }
    }

    growTree() {
      if (level > 0.01) {
        this.lastGrown = new Date().getTime();
        this.size = p.lerp(this.size, this.size + level * 800, 0.1);
        this.velocityY = p.lerp(
          this.velocityY,
          p.random(15, 30) * (p.random(0, 1) > 0.5 ? 1 : -1),
          0.3,
        );
        this.velocityX = p.lerp(
          this.velocityX,
          p.random(15, 30) * (p.random(0, 1) > 0.5 ? 1 : -1),
          0.3,
        );
      }

      if (level > 0.1 && Math.random() < 0.01) {
        const positionsArr =
          positions[Math.floor(p.random(0, positions.length - 1))];
        const {x, y} = positionsArr[
          Math.floor(p.random(0, positionsArr.length - 1))
        ];
        this.initialX = x; // - p.width;
        this.initialY = y;
        // console.log(x, y)
      }

      // if (level > 0.1 && mouseMovedInLast2Seconds(p) && Math.random() < 0.05) {
      //   this.initialX = p.mouseX;
      //   this.initialY = p.mouseY;
      // }
    }

    display() {
      p.noStroke();
      p.fill(this.color);
      p.ellipse(-p.width / 2 + this.x, -p.height / 2 + this.y, this.size);
    }
  }

  p.touchStarted = function touchStarted() {
    p.getAudioContext().resume();
  };
}



// WEBPACK FOOTER //
// ./src/sketches/tree-of-sound.js